/* eslint-disable react/jsx-no-useless-fragment */
import * as React from 'react';
import { BiometryType, NativeBiometric } from '@capgo/capacitor-native-biometric';
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Device } from '@capacitor/device';
import faceIdV2Svg from '../../../images/faceIdV2.svg';
import checkGreenSvg from '../../../images/checkGreen.svg';
import touchIdSvg from '../../../images/touchId.svg';
import faceIdFailedV2Svg from '../../../images/faceIdFailedV2.svg';
import useAsyncAction from '../../../commonComponents/Hooks';
import Helmet from '../../../commonComponents/Helmet';
import { skipBiometricLoginSetup, updateBiometricUniqueId } from '../../../core/api';
import { dashboardPageUrl } from '../../../core/constants';
import { BiometricUniqueIdModel } from '../../../models/AccountModels';
import { getBiometricTypeText } from '../../../core/domain';
import { isWebApp } from '../../../lib/utils';
import AutoLogoutLayout from '../common/Layout';
import BiometricPasswordForm from './forms/BiometricPasswordForm';
import SubPageLayout from '../common/SubPageLayout';
import { BiometricLoginSetupFlowSubPages } from '../../../core/enums';
import { useNavigateWithAppName } from '../../common/NavigateWithAppName';
import { hasValue } from '../../../lib/lib';

// TODO: Should separate the Password verification page from this page component.
// 2 use cases:
// 1. Setup FaceID in login flow, then need to pass in email and password in url state, and no need to
//    show Password verification page.
// 2. Enable FaceId in sidebar menu, Password verification page should return the email and password
const BiometricLoginSetupPage = () => {
	const navigate = useNavigateWithAppName();
	const location = useLocation();
	const { state } = location; // Should pass in password in state for login flow

	const [biometricType, setBiometricType] = useState<BiometryType>();
	const [biometricTypeText, setBiometricTypeText] = useState<string>();
	const [biometricTypeIcon, setBiometricTypeIcon] = useState<'*.svg'>();
	const [email, setEmail] = useState<string>(state?.email);
	const [password, setPassword] = useState<string>(state?.password);

	const setupFromLoginFlow = (state?.fromLogin as boolean | undefined) ?? false;

	// Keep the sequential sub pages in biometric login setup flow:
	const [showVerifyPasswordSubPage, setShowVerifyPasswordSubPage] = React.useState(!setupFromLoginFlow);
	const [showSetupInitialSubPage, setShowSetupInitialSubPage] = React.useState(setupFromLoginFlow);
	const [showSetupSuccessfulSubPage, setShowSetupSuccessfulSubPage] = useState(false);
	const [showSetupFailedSubPage, setShowSetupFailedSubPage] = useState(false);

	const performAsyncAction = useAsyncAction();
	const { signal } = new AbortController();

	React.useEffect(() => {
		if (hasValue(state?.email) && hasValue(state?.password)) {
			setEmail(state.email);
			setPassword(state.password);
		}
	}, [state]);

	React.useEffect(() => {
		performAsyncAction(async () => {
			if (isWebApp) return;

			const result = await NativeBiometric.isAvailable();
			if (!result.isAvailable) return;

			setBiometricType(result.biometryType);
			setBiometricTypeText(getBiometricTypeText(result.biometryType));
			setBiometricTypeIcon(getBiometricTypeIcon(result.biometryType));
		});
	}, []);

	const showSubPage = (nextSubPage: BiometricLoginSetupFlowSubPages) => {
		setShowVerifyPasswordSubPage(nextSubPage === BiometricLoginSetupFlowSubPages.VerifyPasswordSubPage);
		setShowSetupInitialSubPage(nextSubPage === BiometricLoginSetupFlowSubPages.SetupInitialSubPage);
		setShowSetupSuccessfulSubPage(nextSubPage === BiometricLoginSetupFlowSubPages.SetupSuccessfulSubPage);
		setShowSetupFailedSubPage(nextSubPage === BiometricLoginSetupFlowSubPages.SetupFailedSubPage);
	};

	const handleSetupBiometricClick = async () => {
		await performAsyncAction(async () => {
			const verified = await verifyBiometric(biometricType);
			if (!verified) {
				// If setup fails during login flow, automatically skip to prevent infinite loop
				// User can still enable biometrics later from settings
				if (setupFromLoginFlow) {
					await skipBiometricLoginSetup(signal);
				}
				showSubPage(BiometricLoginSetupFlowSubPages.SetupFailedSubPage);
				return;
			}

			const deviceId = await Device.getId();
			await updateBiometricUniqueId({ uniqueId: deviceId.identifier } as BiometricUniqueIdModel, signal);
			// Save credentials to device
			await NativeBiometric.setCredentials({
				username: email,
				password,
				server: deviceId.identifier
			}).then(() => {
				showSubPage(BiometricLoginSetupFlowSubPages.SetupSuccessfulSubPage);
			});
		});
	};

	const handleSuccessfulPasswordVerification = async (newEmail: string, newPassword: string) => {
		setEmail(newEmail);
		setPassword(newPassword);
		showSubPage(BiometricLoginSetupFlowSubPages.SetupInitialSubPage);
	};

	const handleSkipClick = async () => {
		await performAsyncAction(async () => {
			await skipBiometricLoginSetup(signal);
			navigate(dashboardPageUrl, { replace: true });
		});
	};

	const handleContinueClick = () => {
		navigate(dashboardPageUrl, { replace: true });
	};

	return (
		<AutoLogoutLayout
			headerText={!showSetupFailedSubPage ? `Set up ${biometricTypeText}` : undefined}
			onBackButtonClick={!showSetupFailedSubPage ? handleSkipClick : null}
		>
			<Helmet>
				<title>{`Set up ${biometricTypeText}`}</title>
			</Helmet>
			{showVerifyPasswordSubPage && (
				<BiometricPasswordForm onSubmit={handleSuccessfulPasswordVerification} biometricTypeText={biometricTypeText} />
			)}
			{showSetupInitialSubPage && (
				<SubPageLayout
					icon={biometricTypeIcon}
					iconAlt="biometric icon"
					title={`Set up login with ${biometricTypeText}`}
					paragraphs={[
						`Instantly and securely log in with ${biometricTypeText}. You can update this option anytime in the menu.`
					]}
					primaryButtonText={`Setup ${biometricTypeText}`}
					onPrimaryButtonClick={handleSetupBiometricClick}
				/>
			)}
			{showSetupSuccessfulSubPage && (
				<SubPageLayout
					icon={checkGreenSvg}
					iconAlt="login successful form icon"
					title={`${biometricTypeText} set up complete`}
					paragraphs={[`${biometricTypeText} is now set up on your device.`]}
					primaryButtonText="Continue"
					onPrimaryButtonClick={handleContinueClick}
				/>
			)}
			{showSetupFailedSubPage && (
				<SubPageLayout
					icon={faceIdFailedV2Svg}
					iconAlt="Face ID failed icon"
					title={`${biometricTypeText} setup failed`}
					paragraphs={[`${biometricTypeText} setup failed. Please try again or return to the dashboard.`]}
					primaryButtonText="Try again"
					onPrimaryButtonClick={handleSetupBiometricClick}
					secondaryButtonText="Return to dashboard"
					onSecondaryButtonClick={handleSkipClick}
				/>
			)}
		</AutoLogoutLayout>
	);
};

export default BiometricLoginSetupPage;

export const verifyBiometric = async (biometricType: BiometryType) => {
	const verified = await NativeBiometric.verifyIdentity({
		reason: `This app uses ${getBiometricTypeText(biometricType)} to confirm your identity.`
	})
		.then(() => true)
		.catch(() => false);
	return verified;
};

const getBiometricTypeIcon = (type: BiometryType) => {
	switch (type) {
		case BiometryType.FACE_ID:
		case BiometryType.FACE_AUTHENTICATION:
			return faceIdV2Svg;
		case BiometryType.TOUCH_ID:
		case BiometryType.FINGERPRINT:
			return touchIdSvg;
		default:
			return faceIdV2Svg; // TODO: Will need to handle this for Android in the future
	}
};
